import { CollapsingTeamscaleLink } from 'ts/base/components/CollapsingTeamscaleLink';
import { TeamscaleLink } from 'ts/base/routing/TeamscaleLink';
import type { CompareContentInfo } from 'ts/commons/code/compare/CompareContentSide';
import { DateUtils } from 'ts/commons/DateUtils';
import { Links } from 'ts/commons/links/Links';
import { PathUtils } from 'ts/commons/PathUtils';
import { UniformPath } from 'ts/commons/UniformPath';
import type { CommitDescriptor } from 'typedefs/CommitDescriptor';
import type { RepositoryLogEntry } from 'typedefs/RepositoryLogEntry';
import type { WrappedLogEntry } from 'typedefs/WrappedLogEntry';
import styles from './CompareLayout.module.less';

type CompareHeaderProps = {
	content: CompareContentInfo;
	logEntry: RepositoryLogEntry | WrappedLogEntry | null;
	side: 'left' | 'right';
	distributeOverTwoLines?: boolean;
};

/** Displays the caption area for a compare content. */
export function CompareHeader({ content, logEntry, side, distributeOverTwoLines }: CompareHeaderProps) {
	const formattedDate = DateUtils.formatTimestamp(logEntry?.commit.timestamp);
	return (
		<div className={styles.compareHeader} data-side={side} data-testid={side + '-compare-header'}>
			<FileLink
				distributeOverTwoLines={distributeOverTwoLines}
				logEntry={logEntry}
				project={content.project}
				uniformPath={content.uniformPath}
				commit={logEntry?.commit}
			/>
			<div>
				{logEntry ? (
					<>
						<span title={formattedDate}>{formattedDate}</span>
						<br />
						(revision{' '}
						<TeamscaleLink
							className="abbreviated-revision-no"
							to={Links.commitDetails(content.project, logEntry.commit)}
						>
							{logEntry.revision}
						</TeamscaleLink>
						)
					</>
				) : (
					'Unknown date'
				)}
			</div>
		</div>
	);
}

/**
 * Number of characters that easily fit into one line of a compare view header on a slide without a line break. The
 * value was chosen by trial-and-error.
 */
const HEADER_LINE_CHARS_BREAKPOINT = 65;

function FileLink({
	distributeOverTwoLines,
	logEntry,
	project,
	uniformPath,
	commit
}: {
	distributeOverTwoLines?: boolean;
	logEntry: RepositoryLogEntry | WrappedLogEntry | null;
	project: string;
	uniformPath: string;
	commit: CommitDescriptor | undefined;
}) {
	const text = `${project}/${uniformPath}`;
	const linkProps = {
		disabled: logEntry == null,
		uniformPath: text,
		'data-testid': 'file-link',
		to: Links.code(project, uniformPath, { commit })
	};
	if (distributeOverTwoLines) {
		const segments = new UniformPath(uniformPath).getSegmentsWithoutStoragePrefix();
		let firstLine = project;
		let i = 0;
		while (i < segments.length && (firstLine + segments[i]).length < HEADER_LINE_CHARS_BREAKPOINT) {
			firstLine += PathUtils.PACKAGE_SEPARATOR + segments[i++];
		}

		if (i < segments.length) {
			return (
				<span>
					<TeamscaleLink disabled={linkProps.disabled} to={linkProps.to}>
						{firstLine + PathUtils.PACKAGE_SEPARATOR}
					</TeamscaleLink>
					<br />
					<CollapsingTeamscaleLink
						{...linkProps}
						uniformPath={segments.slice(i).join(PathUtils.PACKAGE_SEPARATOR)}
					/>
				</span>
			);
		}
	}
	return <CollapsingTeamscaleLink {...linkProps} />;
}
